import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    // Use requestAnimationFrame for instant zero-delay measurement & animation start
    requestAnimationFrame(() => {
      if (!this.source || !this.destination) {
        this.remove();
        return;
      }

      const sourceRect = this.source.getBoundingClientRect();
      const destinationRect = this.destination.getBoundingClientRect();

      if (sourceRect.width === 0 && sourceRect.height === 0) {
        this.remove();
        return;
      }

      this.#animate(sourceRect, destinationRect);
    });
  }

  /**
   * Animates the flying image along the path to the cart icon.
   * @param {DOMRect} sourceRect - The bounding client rect of the source.
   * @param {DOMRect} destinationRect - The bounding client rect of the destination.
   */
  #animate = (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    // Clean up element smoothly once CSS animation ends
    this.addEventListener('animationend', () => this.remove(), { once: true });

    // Fallback safety cleanup after animation completes
    setTimeout(() => {
      if (this.isConnected) this.remove();
    }, 700);
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
